import { last } from "lodash";

import { logError, logEvent } from "../analytics";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;

export async function downloadDocument(baseUrl: string, name: string) {
  if (!baseUrl) {
    return;
  }

  const url = baseUrl.replace(/^http:\/\//i, "https://");
  const response = await fetchWithRetry({ url, retries: MAX_RETRIES, delayMs: RETRY_DELAY_MS });

  const file = await response.blob();
  // eslint-disable-next-line unicorn/prefer-global-this
  const source = window.URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = source;
  const urlArray = file.type.toLowerCase().split("/");
  const extension = last(urlArray);

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  link.setAttribute("download", `${name}.${extension}`);
  document.body.append(link);
  link.click();
}

function isRetryableError(error: unknown): boolean {
  // Only retry network failures (status_code: 0 in RUM), not HTTP 4xx/5xx
  if (error instanceof TypeError) {
    return true;
  }

  if (error instanceof Error && error.message === "Failed to fetch") {
    return true;
  }

  return false;
}

async function fetchWithRetry(params: {
  url: string;
  retries: number;
  delayMs: number;
}): Promise<Response> {
  const { url, retries, delayMs } = params;
  let urlPath: string;
  try {
    urlPath = new URL(url).pathname;
  } catch {
    urlPath = url;
  }

  const logContext = { urlPath, maxRetries: retries };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Using fetch with cache: "no-store" to bypass the browser cache.
      // This avoids a CORS error that occurs when the same URL was previously
      // loaded by an <img> tag (no-cors mode), causing the browser to serve
      // a cached response without CORS headers for this cors-mode request.
      const response = await fetch(url, { mode: "cors", cache: "no-store" });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (attempt > 0) {
        logEvent("App V2 Document download succeeded after retry", {
          ...logContext,
          attempt,
        });
      }

      return response;
    } catch (error: unknown) {
      const isLastAttempt = attempt >= retries;
      const retryable = isRetryableError(error);

      if (isLastAttempt || !retryable) {
        if (retryable) {
          // Retries exhausted on a transient network failure (`status_code: 0` /
          // "TypeError: Failed to fetch" in RUM). The user's connection dropped
          // mid-download — they typically retry and succeed — so this is not an
          // actionable defect. Routing it through `logError`
          // (`datadogRum.addError`) floods Error Tracking, which groups by the
          // raw error and ignores the `retryable` discriminator, surfacing this
          // same download path (added by ET P19, PR #5052) under a separate
          // group keyed on the bare `TypeError` (issue a3d78038-...,
          // STAFF-1348 / ET P24). Record a non-error RUM action instead — same
          // sink as the sibling retry/success events in this file — so the
          // network-failure rate stays observable while Error Tracking stays
          // clean. The throw below is preserved so callers keep their retry UI.
          logEvent("App V2 Document download network failure", {
            ...logContext,
            attempt,
            error: error instanceof Error ? error.message : String(error),
          });
        } else {
          // Unexpected, non-retryable failure (e.g. HTTP 4xx/5xx). Keep it in
          // Error Tracking so genuine download defects remain observable.
          logError("App V2 Document download failed", {
            error,
            metadata: { ...logContext, attempt, retryable: false },
          });
        }

        throw error;
      }

      logEvent("App V2 Document download retrying", {
        ...logContext,
        attempt,
        error: error instanceof Error ? error.message : String(error),
      });

      await new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    }
  }

  // Unreachable, but TypeScript needs it
  throw new Error("Unexpected: exhausted retries without throwing");
}
